import { Alert, Button, Form, InputGroup, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import ConfirmDeleteElementDialog from "./ConfirmDeleteElementDialog";
import { useFormContext } from "./formElements";

export default function OfferTablePriceList(props) {
  const element = props.value;
  const columns = element.columns;
  const values = element.value;
  const { onChange, onDelete, tableTextAsTextarea } = useFormContext();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isAutomaticCountingDisabled = element.automaticCountingDisabled === true;

  const calculateTotal = (rows = values) => {
    const sumColumnIndex = columns.findIndex((column) => column.type === "sum");

    return rows
      .reduce((total, row) => {
        return total + Number(row[sumColumnIndex].value.replace(" zł", ""));
      }, 0);
  };

  const calculateRowSum = (row) => {
    const unitPriceColumnIndex = columns.findIndex((column) => column.name === "unitPrice");
    const quantityColumnIndex = columns.findIndex((column) => column.name === "quantity");

    const unitPrice = row[unitPriceColumnIndex].value;
    const quantity = row[quantityColumnIndex].value;

    return Number(unitPrice) * Number(quantity) * Number(getTotalMultiplier());
  };

  const formatPrice = (value) => `${Number(value).toFixed(2)} zł`;

  const getTotalMultiplier = () => {
    return element.totalMultiplier === undefined ? 1.0 : element.totalMultiplier;
  };

  const handleSetTotalMultiplier = (event) => {
    element.totalMultiplier = event.target.value;

    if (isAutomaticCountingDisabled) {
      onChange(element);
      return;
    }

    const nextValues = element.value.map((row) => updateCalculatedCells(row));
    element.value = nextValues;
    updateTotalPrice(nextValues);
    onChange(element);
  };

  const updateCalculatedCells = (row) => {
    return row.map((column) =>
      column.type === "sum"
        ? { ...column, value: formatPrice(calculateRowSum(row)) }
        : column
    );
  };

  const updateCell = (rowIndex, columnIndex, column, value) => {
    const nextValues = values.map((row, currentRowIndex) => {
      if (currentRowIndex !== rowIndex) return row;

      const nextRow = row.map((currentColumn, currentColumnIndex) =>
        currentColumnIndex === Number(columnIndex)
          ? { ...column, value }
          : currentColumn
      );

      return isAutomaticCountingDisabled ? nextRow : updateCalculatedCells(nextRow);
    });

    if (!isAutomaticCountingDisabled) {
      updateTotalPrice(nextValues);
    }

    onChange({ ...element, value: nextValues });
  };

  const updateCalculatedCellManually = (rowIndex, columnIndex, column, value) => {
    const nextValues = values.map((row, currentRowIndex) => {
      if (currentRowIndex !== rowIndex) return row;

      return row.map((currentColumn, currentColumnIndex) =>
        currentColumnIndex === Number(columnIndex)
          ? { ...column, value }
          : currentColumn
      );
    });

    element.automaticCountingDisabled = true;
    element.value = nextValues;
    onChange(element);
  };

  const handleSetTotal = (event) => {
    element.automaticCountingDisabled = true;
    element.total = event.target.value;
    onChange(element);
  };

  const updateTotalPrice = (rows) => {
    element.total = formatPrice(calculateTotal(rows));
  };

  const getRow = (index) => {
    let cols = [];
    const data = values[index];

    for (let x in data) {
      const column = data[x];

      if (column.type === "checkbox") {
        cols.push(
          <td className={"p-0"} key={x} width={column.defaultWidth}>
            <Form.Check
              className="m-2"
              type="checkbox"
              checked={column.value}
              onChange={(event) => {
                updateCell(index, x, column, event.target.checked);
              }}
            />
          </td>
        );
      } else if (column.type === "select") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Select
              className={"border-0 bg-transparent"}
              defaultValue={column.value}
              onChange={(event) => {
                updateCell(index, x, column, event.target.value);
              }}
            >
              {column.options.map((opt) => {
                return (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                );
              })}
            </Form.Select>
          </td>
        );
      } else if (column.type === "autocomplete") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className="border-0 bg-transparent"
              list={`options-${x}`}
              defaultValue={column.value}
              onChange={(event) => {
                updateCell(index, x, column, event.target.value);
              }}
            />
            <datalist id={`options-${x}`}>
              {column.options.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          </td>
        );
      } else if (column.type === "textarea") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className={"border-0 bg-transparent"}
              as={"textarea"}
              type={column.type}
              value={column.value}
              placeholder={column.label}
              onChange={(event) => {
                updateCell(index, x, column, event.target.value);
              }}
            />
          </td>
        );
      } else if (column.type === "sum") {
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className={"border-0 bg-transparent"}
              type="text"
              value={column.value}
              onChange={(event) => {
                updateCalculatedCellManually(index, x, column, event.target.value);
              }}
            />
          </td>
        );
      } else {
        const renderAsTextarea = tableTextAsTextarea && column.type === "text";
        cols.push(
          <td className={"p-0"} key={x}>
            <Form.Control
              className={`border-0 bg-transparent${renderAsTextarea ? " offer-table-cell-control" : ""}`}
              as={renderAsTextarea ? "textarea" : undefined}
              rows={renderAsTextarea ? 1 : undefined}
              type={column.type}
              value={column.value}
              placeholder={column.label}
              onChange={(event) => {
                updateCell(index, x, column, event.target.value);
              }}
            />
          </td>
        );
      }
    }

    const handleDeleteRow = () => {
      element.value.splice(index, 1);

      if (!isAutomaticCountingDisabled) {
        updateTotalPrice(element.value);
      }

      onChange({ ...element });
    };

    return (
      <tr key={index}>
        <td>{index + 1}</td>
        {cols}
        <td>
          <div className={"btn-close"} onClick={handleDeleteRow}></div>
        </td>
      </tr>
    );
  };

  const handleAddRow = () => {
    element.value.push(isAutomaticCountingDisabled ? [...columns] : updateCalculatedCells([...columns]));

    if (!isAutomaticCountingDisabled) {
      updateTotalPrice(element.value);
    }

    onChange(element);
  };

  const handleShowTotalAfterDiscount = () => {
    element.totalAfterDiscount = "";
    onChange(element);
  };

  const handleSetTotalAfterDiscount = (event) => {
    element.totalAfterDiscount = event.target.value;
    onChange(element);
  };

  const handleRemoveTotalAfterDiscount = () => {
    delete element.totalAfterDiscount;
    onChange(element);
  };

  const getEmptyCells = (reservedCells = 0) => {
    const visibleColumnsCount = columns.length + 2;
    return Array.from({ length: visibleColumnsCount - 4 - reservedCells }).map((_, index) => (
      <td key={index}></td>
    ));
  };

  return (
    <div className={"m-2"}>
      <div className="d-flex align-items-center justify-content-between pb-2">
        <h5>{element.label}</h5>
        {element.deletable === true ? (
          <Button variant="outline-danger" size="sm" onClick={() => setShowDeleteDialog(true)}>
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        ) : null}
      </div>
      <ConfirmDeleteElementDialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => onDelete(element)}
      />
      <Table striped bordered hover>
        <thead>
          <tr>
            <td>L.p.</td>
            {columns.map((col, i) => {
              return (
                <th key={i} className="p-0">
                  <Form.Control
                    className={"border-0 bg-transparent"}
                    type="text"
                    value={col.label}
                    onChange={(event) => {
                      element.columns[i] = {
                        ...col,
                        label: event.target.value,
                      };
                      onChange(element);
                    }}
                  />
                </th>
              );
            })}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {values.map((row, index) => {
            return getRow(index);
          })}
          <tr>
            {getEmptyCells(1)}
            <td colSpan={2}>Łącznie</td>
            <td className={"p-0"}>
              <InputGroup >
                <InputGroup.Text  className={"border-0 bg-transparent"}>mnożnik: </InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.1"
                   className={"border-0 bg-transparent"}
                  value={getTotalMultiplier()}
                  onChange={handleSetTotalMultiplier}
                />
              </InputGroup>
            </td>
            <td className={"p-0"}>
              <Form.Control
                className={"border-0 bg-transparent"}
                value={element.total}
                onChange={handleSetTotal}
              />
            </td>
          </tr>
          {element.totalAfterDiscount !== undefined ? (
            <tr>
              {getEmptyCells(1)}
              <td colSpan={3}>Łącznie po rabacie</td>
              <td className={"p-0"}>
                <Form.Control
                  className={"border-0 bg-transparent"}
                  value={element.totalAfterDiscount}
                  onChange={handleSetTotalAfterDiscount}
                />
              </td>
              <td>
                <div className={"btn-close"} onClick={handleRemoveTotalAfterDiscount}></div>
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
      {isAutomaticCountingDisabled ? (
        <Alert variant="warning" className="py-2 mb-2">
          Automatyczne zliczanie zosta&#322;o wy&#322;&#261;czone po r&#281;cznej edycji sumy lub pola &#322;&#261;cznie.
        </Alert>
      ) : null}
      <Button variant={"outline-secondary"} onClick={handleAddRow}>
        <FontAwesomeIcon icon={faPlus} /> dodaj wiersz
      </Button>
      {element.totalAfterDiscount === undefined ? (
        <Button variant={"outline-secondary"} className="ms-2" onClick={handleShowTotalAfterDiscount}>
          <FontAwesomeIcon icon={faPlus} /> Łącznie po rabacie
        </Button>
      ) : null}
    </div>
  );
}
